import { useTranslation } from "react-i18next"
import { Copy, Warning } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { describeLookupFailure } from "@/modules/admin/sasis-lookup/describe-lookup-failure"
import { formatXml } from "@/modules/admin/sasis-lookup/format-xml"
import { LookupResultEntry, ViewMode } from "@/modules/admin/sasis-lookup/types/lookup-result-entry"

type LookupResultCardProps = {
  entry: LookupResultEntry
  viewMode: ViewMode
  onCopy: (text: string) => void
}

export const LookupResultCard = ({ entry, viewMode, onCopy }: LookupResultCardProps) => {
  const { t } = useTranslation()

  const formattedMapped = entry.mapped ? JSON.stringify(entry.mapped, null, 2) : null
  const formattedRaw = entry.rawXml ? formatXml(entry.rawXml) : null

  const shownResult = viewMode === "mapped" ? formattedMapped : formattedRaw
  const textToCopy = viewMode === "mapped" ? formattedMapped : entry.rawXml
  const failureReason = entry.mapped
    ? null
    : describeLookupFailure(t, entry.returnCode, entry.returnMessage, entry.errorMessage)

  return (
    <div className="border-border rounded-md border p-4" data-testid="sasis-result-card">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-medium">{entry.organisationName ?? t("admin.sasisLookup.result")}</h3>
        {textToCopy ? (
          <Button variant="tertiary" onClick={() => onCopy(textToCopy)} data-testid="sasis-copy-button">
            <Copy className="size-4" />
            {t("admin.sasisLookup.copy")}
          </Button>
        ) : null}
      </div>

      {failureReason ? (
        <div
          className="text-on-warning-surface bg-warning-surface mb-2 flex items-center gap-2 rounded-md p-2 text-sm"
          data-testid="sasis-result-failure">
          <Warning className="size-4 shrink-0" weight="fill" />
          <p>{failureReason}</p>
        </div>
      ) : null}

      {shownResult ? (
        <pre className="bg-surface-subdued max-h-150 overflow-auto rounded-md p-4 text-sm" data-testid="sasis-result">
          {shownResult}
        </pre>
      ) : null}
    </div>
  )
}
